import AscCalendarMonthView from "./AscCalendarMonthView/AscCalendarMonthView";
import AscButton from "../AscButton/AscButton";
import styles from "./AscCalendarEvents.module.scss";
import { ArrowLeftIcon, ArrowRightIcon } from "../AscIcons";
import {
  CalendarDate,
  useCalendarDays,
} from "../../lib/helpers/hooks/useCalendarDays";
import {
  AscCalendarEvent,
  AscCalendarEventsProps,
} from "./AscCalendarEvents.types";
import AscCalendarEventItem from "./AscCalendarEventItem/AscCalendarEventItem";
import AscCalendarEventsModal from "./AscCalendarEventsModal/AscCalendarEventsModal";
import { useState } from "react";
import dayjs from "dayjs";
import classNames from "classnames";

function AscCalendarEvents({
  events,
  maxUpcomingEvents = 2,
  showSeeMoreButtonInModal = true,
  loading = false,
  onSeeMore,
}: AscCalendarEventsProps) {
  const {
    goToPreviousMonths,
    goToNextMonths,
    calendarDaysForMonth,
    activeMonthLabel,
    weekdayLabels,
    upcomingEvents,
    getEventsByDate,
  } = useCalendarDays({ events });
  const [selectedDate, setSelectedDate] = useState<{
    date: string;
    events: AscCalendarEvent[];
  }>();

  function closeCalendarEventsModal() {
    setSelectedDate(undefined);
  }

  function onEventClick(date: string) {
    const events = getEventsByDate(date);
    setSelectedDate({ date, events });
  }

  function onDayClick(day: CalendarDate) {
    setSelectedDate({
      date: dayjs(day?.date).toISOString(),
      events: day?.events || [],
    });
  }

  return (
    <div
      className={classNames(styles.ground, {
        [styles.loading]: loading,
      })}
      data-testid="asc-calendar-events"
    >
      <div className={styles.heading}>
        <div className={styles.monthLabel}>{activeMonthLabel}</div>
        <div className={styles.navigationContainer}>
          <AscButton
            size="small"
            variant={"link"}
            onClick={goToPreviousMonths}
            hideLinkIcon
          >
            <ArrowLeftIcon className={styles.navigationIcon} />
          </AscButton>
          <AscButton
            size="small"
            variant={"link"}
            onClick={goToNextMonths}
            hideLinkIcon
          >
            <ArrowRightIcon className={styles.navigationIcon} />
          </AscButton>
        </div>
      </div>

      <div className={styles.calendarContainer}>
        <AscCalendarMonthView
          calendarDaysForMonth={calendarDaysForMonth}
          weekdayLabels={weekdayLabels}
          onDayClick={onDayClick}
        />
      </div>
      {upcomingEvents?.length ? (
        <div className={styles.upcomingEvents}>
          <div className={styles.upcomingEventsTitle}>Upcoming</div>
          {upcomingEvents?.slice(0, maxUpcomingEvents)?.map((event, index) => (
            <AscCalendarEventItem
              key={index}
              title={event?.title}
              date={event?.date}
              type={event?.type}
              onEventClick={onEventClick}
            />
          ))}
        </div>
      ) : null}

      <AscCalendarEventsModal
        visible={!!selectedDate?.events?.length}
        date={dayjs(selectedDate?.date).toISOString()}
        setVisible={closeCalendarEventsModal}
        events={selectedDate?.events || []}
        showSeeMoreButton={showSeeMoreButtonInModal}
        onSeeMore={onSeeMore}
      />
    </div>
  );
}

export default AscCalendarEvents;
